import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show, useAuth } from "@clerk/react";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, Package, IndianRupee, CheckCircle2, Clock } from "lucide-react";
import { api, formatINR, STATUS_LABELS, STATUS_STYLES, issuesText } from "@/lib/api";
import { CatalogManager } from "@/components/admin/CatalogManager";

export default function AdminDashboard() {
  return (
    <>
      <Show when="signed-out">
        <Navigate to="/admin/sign-in" replace />
      </Show>
      <Show when="signed-in">
        <AdminDashboardInner />
      </Show>
    </>
  );
}

function AdminDashboardInner() {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [orders, setOrders] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [stats, setStats] = useState(null);
  const [issuePct, setIssuePct] = useState(null);
  const [search, setSearch] = useState("");

  const auth = async () => ({
    headers: { Authorization: `Bearer ${await getToken()}` },
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/me", await auth());
        setMe(res.data);
      } catch {
        setMe({ role: null });
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      const [o, p, s, st] = await Promise.all([
        api.get("/admin/orders", await auth()),
        api.get("/admin/catalog", await auth()),
        api.get("/admin/stats", await auth()),
        api.get("/admin/settings", await auth()),
      ]);
      setOrders(o.data.orders);
      setCatalog(p.data.brands);
      setStats(s.data);
      setIssuePct(st.data.issue_deduction_pct);
    } catch (e) {
      toast.error("Could not load admin data");
    }
  };

  useEffect(() => {
    if (me?.role === "admin") loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  if (checking) {
    return <main className="min-h-[60vh] grid place-items-center text-slate-500 text-sm">Loading…</main>;
  }

  if (me?.role !== "admin") {
    return (
      <main className="min-h-[calc(100vh-5rem)] grid place-items-center px-4">
        <div className="max-w-md w-full p-8 rounded-2xl bg-white border border-slate-200 text-center">
          <ShieldAlert className="w-10 h-10 mx-auto text-[#0e9f6e] mb-3" />
          <h1 className="font-display text-xl font-bold text-slate-900">Not an admin</h1>
          <p className="mt-2 text-sm text-slate-600">
            This account is signed in but doesn’t have admin access. Ask the owner to grant it.
          </p>
        </div>
      </main>
    );
  }

  const changeStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status }, await auth());
      toast.success("Status updated");
      loadAll();
    } catch {
      toast.error("Could not update status");
    }
  };

  const request = async (method, url, data) =>
    method === "delete" || method === "get"
      ? api[method](url, await auth())
      : api[method](url, data, await auth());

  const updateIssuePct = async (pct) => {
    try {
      const res = await api.put("/admin/settings", { issue_deduction_pct: Number(pct) }, await auth());
      setIssuePct(res.data.issue_deduction_pct);
      toast.success("Problem deduction saved");
    } catch {
      toast.error("Enter a value between 0 and 50");
    }
  };

  const filteredOrders = orders.filter((o) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      o.customer.name.toLowerCase().includes(q) ||
      o.customer.phone.includes(q) ||
      `${o.brand_name} ${o.model_name}`.toLowerCase().includes(q) ||
      o.id.toLowerCase().includes(q)
    );
  });

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#1e429f]">
              Admin Dashboard
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-0.5">
              TechSaathi Control Center
            </h1>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <StatCard icon={Package} label="Total orders" value={stats?.total_orders ?? "—"} />
          <StatCard
            icon={IndianRupee}
            label="Total value"
            value={stats ? formatINR(stats.total_value) : "—"}
          />
          <StatCard
            icon={Clock}
            label="New"
            value={stats?.by_status?.new?.count ?? 0}
          />
          <StatCard
            icon={CheckCircle2}
            label="Paid"
            value={stats?.by_status?.paid?.count ?? 0}
          />
        </div>

        <Tabs defaultValue="orders">
          <TabsList className="mb-4 bg-white border border-slate-200">
            <TabsTrigger value="orders" data-testid="lcost-admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="prices" data-testid="lcost-admin-tab-prices">Phone catalog</TabsTrigger>
          </TabsList>

          <TabsContent value="orders">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <Input
                  placeholder="Search by name, phone, model, order id…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-md"
                  data-testid="lcost-admin-orders-search"
                />
              </div>
              <div className="overflow-x-auto">
                <Table data-testid="lcost-admin-orders-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Pickup</TableHead>
                      <TableHead className="text-right">Quote</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <button className="font-mono text-xs text-[#1e429f] hover:underline">
                                #{o.id.slice(0, 8).toUpperCase()}
                              </button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Order #{o.id.slice(0, 8).toUpperCase()}</DialogTitle>
                              </DialogHeader>
                              <OrderDetail order={o} />
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-slate-900 text-sm">
                            {o.brand_name} {o.model_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {o.condition.screen} · {issuesText(o.condition)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-900">{o.customer.name}</div>
                          <div className="text-xs text-slate-500 font-mono">{o.customer.phone}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-900">{o.pickup_date}</div>
                          <div className="text-xs text-slate-500 font-mono">{o.pickup_slot}</div>
                        </TableCell>
                        <TableCell className="text-right font-display font-bold text-[#1e429f]">
                          <span className="bg-[#def7ec] text-[#057a55] px-2 py-0.5 rounded-md">{formatINR(o.quote_amount)}</span>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={o.status}
                            onValueChange={(v) => changeStatus(o.id, v)}
                          >
                            <SelectTrigger
                              className={`h-8 w-[130px] border ${STATUS_STYLES[o.status]}`}
                              data-testid={`lcost-admin-status-select-${o.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredOrders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-slate-500 text-sm">
                          No orders yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="prices">
            {issuePct !== null && <IssueDeductionCard value={issuePct} onSave={updateIssuePct} />}
            <CatalogManager catalog={catalog} request={request} onChanged={loadAll} />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="p-4 sm:p-5 bg-white rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-2xl font-bold text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function OrderDetail({ order }) {
  return (
    <div className="space-y-3 text-sm">
      <Detail label="Device" value={`${order.brand_name} ${order.model_name}`} />
      <Detail label="Quote" value={formatINR(order.quote_amount)} highlight />
      <Detail label="Base price" value={formatINR(order.base_price)} />
      <Detail
        label="Condition"
        value={`Screen: ${order.condition.screen} · Body: ${order.condition.body} · Battery: ${order.condition.battery} · Issues: ${issuesText(order.condition)}`}
      />
      <Detail label="Customer" value={`${order.customer.name} (${order.customer.phone})`} />
      <Detail label="Email" value={order.customer.email} />
      <Detail label="Address" value={`${order.customer.address}, ${order.customer.city}`} />
      <Detail label="Pickup" value={`${order.pickup_date} · ${order.pickup_slot}`} />
      <Detail
        label="Status"
        value={<Badge className={`border ${STATUS_STYLES[order.status]}`}>{STATUS_LABELS[order.status]}</Badge>}
      />
    </div>
  );
}

function Detail({ label, value, highlight }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-1 text-xs text-slate-500">{label}</div>
      <div className={`col-span-2 ${highlight ? "font-display font-bold text-[#057a55] bg-[#def7ec] px-2 rounded-md w-fit" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

function IssueDeductionCard({ value, onSave }) {
  const [draft, setDraft] = useState(value);
  const num = Number(draft);
  const valid = draft !== "" && num >= 0 && num <= 50;
  return (
    <div data-testid="lcost-admin-issue-deduction-card" className="mb-4 p-4 sm:p-5 bg-white rounded-2xl border border-slate-200 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1">
        <div className="font-display font-bold text-slate-900">Price cut per problem</div>
        <div className="text-sm text-slate-500 mt-0.5">
          Applied for each problem a customer selects when the phone is not fully working (0–50%).
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative w-28">
          <Input
            data-testid="lcost-admin-issue-deduction-input"
            type="number" min={0} max={50} step="0.5"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="pr-8 h-10"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
        </div>
        <Button
          data-testid="lcost-admin-issue-deduction-save-btn"
          disabled={!valid || num === Number(value)}
          onClick={() => onSave(draft)}
          className="h-10 btn-brand text-white px-4"
        >
          Save
        </Button>
      </div>
    </div>
  );
}

